import styles from './index.module.css'

import type { CellContext } from '@tanstack/react-table'
import React, { useMemo, useRef } from 'react'

import type { Track } from 'types/track'

import { Flex, Text } from 'components/ds'

import { OverflowHoverCard } from './components/overflow-hover-card'
import { TagDot } from './components/tag-dot'
import { getColoredTags } from './utils/indicator-colors'
import { useVisibleDotCount } from './utils/use-visible-dot-count'

export { INDICATORS_COLUMN_DEFAULT_SIZE } from './utils/use-visible-dot-count'

type Props = { info: CellContext<Track, unknown> }

const IndicatorsColumnComponent = ({ info }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const rawTags = info.row.original.native_tags

  const coloredTags = useMemo(() => getColoredTags(rawTags), [rawTags])
  const totalTags = coloredTags.length

  const visibleCount = useVisibleDotCount(
    containerRef,
    totalTags,
    info.column.getSize(),
  )

  if (totalTags === 0) {
    return (
      <Flex pl="2">
        <Text size="2" color="gray" data-testid="indicators-cell">
          —
        </Text>
      </Flex>
    )
  }

  const visible = coloredTags.slice(0, visibleCount)
  const hiddenCount = totalTags - visible.length

  return (
    <Flex
      ref={containerRef}
      className={styles.container}
      data-testid="indicators-cell"
    >
      <Flex as="span" className={styles.dots}>
        {visible.map((tag, index) => (
          // zIndex keeps the cascade (leftmost dot on top) working for any
          // number of dots, unlike a fixed nth-child rule.
          <TagDot key={tag.id} tag={tag} zIndex={visible.length - index} />
        ))}
      </Flex>
      {hiddenCount > 0 && (
        <OverflowHoverCard tags={coloredTags} hiddenCount={hiddenCount} />
      )}
    </Flex>
  )
}

export const IndicatorsColumn = React.memo(IndicatorsColumnComponent)
IndicatorsColumn.displayName = 'IndicatorsColumn'
